import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import logger from 'js/app/loggerSingleton';
import Retracked from 'js/lib/retracked';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import adsTracker from 'bundles/event-tracking/lib';
import { EOI_SUBMISSION_TRACKER } from 'bundles/expression-of-interest/constants';
import { getUserInfoQuestions, updateUserProfile } from 'bundles/expression-of-interest/utils/util';
import type { SectionData } from 'bundles/premium-hub/types/EoiForm';
import { initializeQualificationSurvey, submitResponse } from 'bundles/premium-hub/utils/QualificationAPIUtils';
import type { QuestionType } from 'bundles/survey-form/constants/SurveyFormConstants';
import { getResponseTypeFromQuestionType, processResponse } from 'bundles/survey-form/utils/SurveyFormUtils';
import { DegreeQualifiedTitle, SectionTypes } from 'bundles/university-program-qualification/utils/constants';
import type { Maybe } from 'bundles/xddp/__generated__/baseContentfulTypes';
import EoiLoading from 'bundles/xddp/components/landing/EoiLoading';
import type { EoiFormProps, EoiTerminalProps, UserInput } from 'bundles/xddp/types';
import {
  getInputfromUserProfile,
  getUserInfoFromEoiResponse,
  getUserInputfromUserInfo,
} from 'bundles/xddp/utils/name-formatting';

type Props = {
  slug?: string;
  degreeId: string;
  productType: string;
  partnerName?: Maybe<string>;
  shortName?: Maybe<string>;
  prepopulateUserInfo: boolean;
};

type State = {
  sectionData: SectionData | null;
  userInput?: UserInput;
  initialUserInput?: {};
  validationErrors: {};
  isSubmitting?: boolean;
  isSubmittingFromLocalStorage?: boolean;
  region: string;
};

const withEoiControls = (
  FormComponent: React.ComponentType<EoiFormProps>,
  TerminalComponent: React.ComponentType<EoiTerminalProps>
) => {
  return class HOC extends React.Component<Props, State> {
    static contextTypes = {
      executeAction: PropTypes.func.isRequired,
      router: PropTypes.object.isRequired,
      _eventData: PropTypes.object,
    };

    declare context: LegacyContextType<typeof HOC.contextTypes>;

    constructor(props: Props) {
      super(props);

      this.state = {
        userInput: undefined,
        initialUserInput: undefined,
        sectionData: null,
        validationErrors: {},
        isSubmitting: false,
        isSubmittingFromLocalStorage: false,
        region: '',
      };
    }

    componentDidMount() {
      this.initializeQualificationSurvey();
    }

    componentDidUpdate() {
      const { sectionData } = this.state;

      if (sectionData && sectionData.section.typeName === SectionTypes.TERMINAL) {
        this.trackDegreeEOICompletion();
      }
    }

    handleInputChangeFunc = (questionId: string, questionType: QuestionType) => {
      const { userInput } = this.state;
      const responseToUpdate = { ...userInput };

      return (response: { text?: string; ids?: Array<string> }) => {
        const responseType = getResponseTypeFromQuestionType(questionType);
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        responseToUpdate[questionId] = {
          [responseType]: response,
        };

        this.setState(() => ({
          userInput: responseToUpdate,
        }));
      };
    };

    setRegion = (newRegion: string) => {
      this.setState({
        region: newRegion,
      });
    };

    onClickNext = () => {
      const { degreeId, productType } = this.props;
      const { executeAction } = this.context;
      const { userInput, sectionData } = this.state;

      if (!sectionData) return;

      const qualificationSurveyProductId = tupleToStringKey([productType, degreeId]);

      const {
        id,
        sectionId,
        section: {
          definition: { questions },
          typeName,
        },
      } = sectionData;

      const processedResponse = processResponse(userInput);
      this.setState(() => ({ isSubmitting: true }));
      executeAction(submitResponse, {
        productId: qualificationSurveyProductId,
        sectionData: {
          requestId: id,
          sectionId,
          typeName,
          questions,
        },
        userInput: processedResponse,
        onSuccess: async (response: $TSFixMe) => {
          const userInfo = getUserInfoFromEoiResponse(processedResponse);

          await updateUserProfile({
            userId: user.get().id,
            externalId: user.get().external_id,
            ...userInfo,
          }).catch((error) => logger.error(error));
          this.setState(() => ({ isSubmitting: false }));
          this.updateSectionData(response);
        },
        onError: (validationErrors: $TSFixMe) => {
          this.setState(() => ({
            isSubmitting: false,
            validationErrors,
          }));
        },
        logAction: (action: string, metadata?: Record<string, string>) =>
          Retracked.trackComponent(
            this.context._eventData,
            { pathname: this.context.router.location.pathname, region: this.state.region, ...metadata },
            EOI_SUBMISSION_TRACKER,
            action
          ),
      });
    };

    trackDegreeEOICompletion = () => {
      const { slug } = this.props;
      const { sectionData } = this.state;

      if (!sectionData) return;

      const {
        id,
        section: {
          typeName,
          definition: { title },
        },
      } = sectionData;

      const isTerminal = typeName === SectionTypes.TERMINAL;
      const isQualified = isTerminal && title === DegreeQualifiedTitle;

      adsTracker.trackDegreeEOICompletion(slug, id, isQualified);
    };

    updateSectionData = (sectionData: SectionData) => {
      this.setState(() => ({ sectionData }));
    };

    getInfoFromUserProfile = async (userInfoInput: $TSFixMe) => {
      const profileInput = await getInputfromUserProfile();

      const userInput = { ...profileInput, ...userInfoInput };

      // Update survey with info from user profile
      this.setState(() => ({
        userInput,
        initialUserInput: userInput,
      }));
    };

    handlePrePopulateSurvey = () => {
      const { sectionData } = this.state;
      const userInfoInput = getUserInputfromUserInfo();

      const surveyQuestions = sectionData?.section?.definition.questions;
      const surveyHasPhoneQuestion =
        surveyQuestions && surveyQuestions.some((question) => question.id === 'phoneNumber');

      if (surveyHasPhoneQuestion) {
        this.getInfoFromUserProfile(userInfoInput);
      } else {
        // If the user is authenticated, update survey with info from user obj
        this.setState(() => ({
          userInput: userInfoInput,
          initialUserInput: userInfoInput,
        }));
      }
    };

    initializeQualificationSurvey = () => {
      const { degreeId, productType } = this.props;
      const { executeAction } = this.context;

      const qualificationSurveyProductId = tupleToStringKey([productType, degreeId]);

      executeAction(initializeQualificationSurvey, {
        productId: qualificationSurveyProductId,
        onSuccess: (response: $TSFixMe) => {
          this.updateSectionData(response);

          if (user.isAuthenticatedUser()) this.handlePrePopulateSurvey();
        },
      });
    };

    render() {
      const { slug, partnerName, shortName } = this.props;
      const { sectionData, validationErrors, userInput, initialUserInput, isSubmittingFromLocalStorage } = this.state;

      if (!sectionData || !userInput || isSubmittingFromLocalStorage) {
        return <EoiLoading />;
      }

      const {
        section: {
          definition: { questions },
          typeName,
        },
      } = sectionData;

      if (typeName === SectionTypes.TERMINAL) {
        return <TerminalComponent slug={slug} partnerName={partnerName} shortName={shortName} />;
      }

      const userInfoQuestions = getUserInfoQuestions(questions, initialUserInput);
      return (
        <div id="eoi">
          <FormComponent
            shortName={shortName}
            partnerName={partnerName}
            handleInputChangeFunc={this.handleInputChangeFunc}
            validationErrors={validationErrors}
            questions={userInfoQuestions}
            onClickNext={this.onClickNext}
            isSubmitting={this.state.isSubmitting}
            setRegion={this.setRegion}
          />
        </div>
      );
    }
  };
};

export default withEoiControls;
